import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Input, Select, Space, Tag, Empty, message } from 'antd';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import ProjectCard from '../../../components/cards/projects/ProjectCard';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useProjectApi } from '../../../api/projects/projects';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { TProject, TProjectLike } from '../../../models/project/project';

const { Search } = Input;
const { Option } = Select;

/* Helpers */
const getSchoolYear = (iso: string) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  return d.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

const OrganisationStore: React.FC = () => {

  const { t } = useTranslation();
  const { getSchoolStore, getLikesByUserId } = useProjectApi();
  const { getCodificationsByCodeType } = useReferential();
  const { getUserAppId } = useTilmiAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<TProject[]>([]);
  const [likes, setLikes] = useState<TProjectLike[]>([]);
  /* filtres */
  const [searchProject, setSearchProject] = useState('');
  const [filterType, setFilterType] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<string | null>(null);


  /* Chargement */
  useEffect(() => {
    setLoading(true)
    const userId = getUserAppId();   // ← on récupère l’ID de l’utilisateur
    Promise.all([getSchoolStore(), getLikesByUserId(userId)])
      .then(([projRes, likesRes]) => {
        const projs = projRes.data ?? [];
        const lks = likesRes.data ?? [];
        setLikes(lks)
        setProjects(projs);
      })
      .catch(err => message.error(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  /* Codifs type projet */
  const projectTypeCodifs = useMemo(
    () => getCodificationsByCodeType(CODIFICATION_TYPE.PROJECT_TYPE),
    [getCodificationsByCodeType]
  );

  /* Filtrage */
  const filtered = useMemo(() =>
    projects.filter(p => {
      if (searchProject && !p.name.toLowerCase().includes(searchProject.toLowerCase())) return false;
      if (filterType && p.project_type_id !== filterType) return false;
      if (filterYear && p.created_at && getSchoolYear(p.created_at) !== filterYear) return false;
      return true;
    }),
    [projects, searchProject, filterType, filterYear]);

  /* Dropdown dynamiques */
  const projectTypes = useMemo(() =>
    Array.from(new Map(projects
      .filter(p => p.project_type_id)
      .map(p => {
        const id = p.project_type_id;
        const codif = projectTypeCodifs.find(c => c.id === id);
        return [id, codif?.label ?? ''];
      }))).map(([id, label]) => ({ id, label })),
    [projects, projectTypeCodifs]);

  const schoolYears = useMemo(() =>
    Array.from(new Set(projects
      .filter(p => p.created_at)
      .map(p => getSchoolYear(p.created_at || "_")))).sort(),
    [projects]);

  return (
    <AppLayout title={t('sidebar.storeProjects', 'Magasin')} loading={loading}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder={t('text.searchProject', 'Recherche projet')}
          onSearch={setSearchProject}
          allowClear
          style={{ width: 220 }}
        />
        <Select
          placeholder={t('text.projectType', 'Type de projet')}
          onChange={setFilterType}
          allowClear
          style={{ width: 180 }}
          value={filterType ?? undefined}
        >
          {projectTypes.map(pt => <Option key={pt.id} value={pt.id}>{pt.label}</Option>)}
        </Select>
        <Select
          placeholder={t('text.schoolYear', 'Année scolaire')}
          onChange={setFilterYear}
          allowClear
          style={{ width: 160 }}
          value={filterYear ?? undefined}
        >
          {schoolYears.map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select>
      </Space>

      {filtered.length === 0 ? (
        <Empty description={t('text.noProject', 'Aucun projet')} />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map(p => (
            <Col key={p.id} xs={24} sm={12} md={8} lg={6} xl={4}>
              {p ? <ProjectCard studentProject={p} likedStatus={likes} /> : <Tag color="red">{t('text.missingData', 'Données manquantes')}</Tag>}
            </Col>
          ))}
        </Row>
      )}
    </AppLayout>
  );
};

export default OrganisationStore;
